import { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/base/VideoCard";
import SEOSchema from "@/components/base/SEOSchema";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<"hot" | "new" | "score">("hot");
  const [activeTab, setActiveTab] = useState<string>(slug || "dianying");

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeTab) || categories[0],
    [activeTab],
  );

  const filteredVideos = useMemo(() => {
    let result = videos.filter((v) => v.categorySlug === activeTab);
    if (sortBy === "hot") {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === "new") {
      result = [...result].sort(
        (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime(),
      );
    } else if (sortBy === "score") {
      result = [...result].sort((a, b) => b.score - a.score);
    }
    return result;
  }, [activeTab, sortBy]);

  return (
    <>
      <SEOSchema
        type="WebPage"
        data={{
          name: `${currentCategory.name} - 17c com一起草在线`,
          description: currentCategory.description,
          url: `https://17c.com/category/${activeTab}`,
        }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="mx-auto max-w-[1400px] px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t("breadcrumbHome"), href: "/" },
                { label: currentCategory.name },
              ]}
            />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500">
                {currentCategory.description} — 共{currentCategory.videoCount.toLocaleString()}部视频
              </p>
            </div>

            <nav aria-label="分类标签" className="mb-8 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max px-1 py-1">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setActiveTab(cat.slug);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      activeTab === cat.slug
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </nav>

            <div className="flex items-center justify-between mb-6">
              <span className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-800">{filteredVideos.length}</strong> 部视频
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400 mr-1">排序：</span>
                {[
                  { key: "hot", label: "最热" },
                  { key: "new", label: "最新" },
                  { key: "score", label: "评分" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key as typeof sortBy)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === opt.key
                        ? "bg-foreground-900 text-background-50"
                        : "bg-background-100 text-foreground-500 hover:bg-background-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 pb-14">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-vidicon-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500">{t("noResults")}</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}